import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslations } from '../context/TranslationsContext';
import { getHomePath, getLocalizedPath } from '../utils/routes';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['auth', 'common', 'errors']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: setAuthState } = useAuth();
  const lang = i18n.language;
  
  // Translation helper
  const t = (key, fallback = null) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  const lp = (key) => getLocalizedPath(key, lang);

  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'expired', 'already_verified', 'fitnet_unavailable'
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError(t('auth.verificationTokenMissing', 'Brakuje tokenu weryfikacyjnego'));
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify-email/${token}`);
      
      if (response.data.success) {
        if (response.data.alreadyVerified) {
          setStatus('already_verified');
        } else {
          setStatus('success');
          setUser(response.data.user);
          
          // Store token and update auth state
          if (response.data.token) {
            localStorage.setItem('avepark_token', response.data.token);
            // Auto-login after 3 seconds
            setTimeout(() => {
              navigate(lp('account'));
            }, 3000);
          }
        }
      }
    } catch (err) {
      console.error('Verification error:', err);
      const errorCode = err.response?.data?.error || '';
      const errorMsg = err.response?.data?.message || 'Verification failed';
      
      if (errorCode === 'fitnet_unavailable') {
        setStatus('fitnet_unavailable');
        setError(errorMsg);
      } else if (errorMsg.toLowerCase().includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
      setError(errorMsg);
    }
  };

  const handleResendEmail = async () => {
    // This would need the email from URL or state
    // For now, redirect to contact or resend page
    navigate(lp('contact'));
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
            </div>
            <h2 className="text-2xl font-bold mb-4">
              {t('auth.verifyingEmail', 'Weryfikacja adresu email...')}
            </h2>
            <p className="text-muted-foreground">
              {t('auth.pleaseWait', 'Proszę czekać...')}
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-4 text-green-400">
              {t('auth.emailVerified', 'Email zweryfikowany!')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('auth.accountActivated', 'Twoje konto zostało aktywowane. Możesz teraz korzystać ze wszystkich funkcji AvePark.')}
            </p>
            {user && (
              <p className="text-sm text-primary mb-4">
                {t('auth.welcomeUser', 'Witaj')}, {user.firstName}!
              </p>
            )}
            <p className="text-sm text-muted-foreground mb-6">
              {t('auth.redirectingToAccount', 'Za chwilę zostaniesz przekierowany do swojego konta...')}
            </p>
            <Link 
              to={lp('account')} 
              className="btn-primary inline-block"
              data-testid="go-to-account"
            >
              {t('auth.goToAccount', 'Przejdź do konta')}
            </Link>
          </div>
        );

      case 'fitnet_unavailable':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold mb-4 text-yellow-400">
              {t('auth.fitnetUnavailableTitle', 'System chwilowo niedostępny')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('auth.fitnetUnavailableDesc', 'System rezerwacji jest chwilowo niedostępny. Twoje konto nie zostało utworzone. Spróbuj ponownie za kilka minut klikając link weryfikacyjny w emailu.')}
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="btn-primary inline-block"
              data-testid="retry-verification"
            >
              {t('common.tryAgain', 'Spróbuj ponownie')}
            </button>
          </div>
        );

      case 'already_verified':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold mb-4">
              {t('auth.alreadyVerified', 'Email już zweryfikowany')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('auth.alreadyVerifiedDesc', 'Ten adres email został już wcześniej zweryfikowany. Możesz się zalogować.')}
            </p>
            <Link 
              to={lp('login')} 
              className="btn-primary inline-block"
              data-testid="go-to-login"
            >
              {t('auth.goToLogin', 'Przejdź do logowania')}
            </Link>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-6">
              <RefreshCw className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold mb-4 text-orange-400">
              {t('auth.linkExpired', 'Link wygasł')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('auth.linkExpiredDesc', 'Link weryfikacyjny wygasł. Zarejestruj się ponownie, aby otrzymać nowy link.')}
            </p>
            <Link 
              to={lp('register')} 
              className="btn-primary inline-block"
              data-testid="register-again"
            >
              {t('auth.registerAgain', 'Zarejestruj się ponownie')}
            </Link>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold mb-4 text-red-400">
              {t('auth.verificationFailed', 'Weryfikacja nie powiodła się')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {error || t('auth.verificationError', 'Wystąpił błąd podczas weryfikacji. Spróbuj ponownie lub skontaktuj się z nami.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to={lp('register')} 
                className="btn-primary"
                data-testid="try-register-again"
              >
                {t('auth.tryAgain', 'Spróbuj ponownie')}
              </Link>
              <Link 
                to={lp('contact')} 
                className="btn-secondary"
                data-testid="contact-support"
              >
                {t('common.contactUs', 'Skontaktuj się')}
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center" data-testid="verify-email-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-12">
          {renderContent()}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
